/**
 * CI Proof Setup Page
 * 5-step onboarding for CI Proof widget configuration
 * 
 * Route: /dashboard/proof/setup
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, ChevronRight, ChevronLeft, Eye, 
  Settings, Palette, Check, ExternalLink, Sparkles,
  Package, Grid, Zap, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import MerchantHeader from '../components/MerchantHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import apiClient from '../services/api';

const SETUP_STEPS = [
  { id: 'enable', title: 'Enable Widget', icon: Zap },
  { id: 'scope', title: 'Choose Scope', icon: Package },
  { id: 'placement', title: 'Placement', icon: Grid },
  { id: 'style', title: 'Style', icon: Palette },
  { id: 'verify', title: 'Verify', icon: Check }
];

export default function ProofSetupPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Proof settings state
  const [settings, setSettings] = useState({
    enabled: false,
    scope: 'free', // free | pro (full catalog)
    selectedProducts: [], // For free tier (max 10)
    placement: 'pdp', // pdp | collection
    theme: 'subtle', // subtle | bold
    size: 'medium', // small | medium
    hasProAddon: false
  });
  
  // Verification state
  const [verificationStatus, setVerificationStatus] = useState({
    checked: false,
    lastEvent: null,
    isLive: false
  });
  
  useEffect(() => {
    loadProofSettings();
  }, []);
  
  const loadProofSettings = async () => {
    setLoading(true);
    const token = sessionStorage.getItem('session_token');
    
    try {
      // Load merchant entitlements
      const res = await apiClient.get('/api/merchant/subscription', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const subscription = res.data;
      const hasProAddon = subscription.addons?.ci_proof_pro === true;
      
      // Load existing proof settings
      const settingsRes = await apiClient.get('/api/merchant/proof/settings', {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: {} }));
      
      setSettings(prev => ({
        ...prev,
        ...settingsRes.data,
        hasProAddon
      }));
      
    } catch (err) {
      console.error('Error loading proof settings:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const saveSettings = async () => {
    setSaving(true);
    const token = sessionStorage.getItem('session_token');
    
    try {
      await apiClient.post('/api/merchant/proof/settings', settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Settings saved successfully');
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const checkVerification = async () => {
    const token = sessionStorage.getItem('session_token');
    
    try {
      const res = await apiClient.get('/api/merchant/proof/verify', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setVerificationStatus({
        checked: true,
        lastEvent: res.data.last_event_at,
        isLive: res.data.is_live
      });
      
      if (res.data.is_live) {
        toast.success('CI Proof is live! Events are being received.');
      } else {
        toast.info('No events detected yet. Make sure the widget is installed correctly.');
      }
    } catch (err) {
      setVerificationStatus({
        checked: true,
        lastEvent: null,
        isLive: false
      });
    }
  };
  
  const nextStep = async () => {
    if (currentStep < SETUP_STEPS.length - 1) {
      await saveSettings();
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const completeSetup = async () => {
    await saveSettings();
    toast.success('CI Proof setup complete!');
    navigate('/dashboard/promise-health');
  };
  
  const progress = ((currentStep + 1) / SETUP_STEPS.length) * 100;
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MerchantHeader />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading CI Proof settings...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background" data-testid="proof-setup-page">
      <MerchantHeader />
      
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Eye className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">CI Proof Setup</h1>
          </div>
          <p className="text-muted-foreground">
            Configure the CI Proof widget for your store
          </p>
        </div>
        
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {SETUP_STEPS.length}
            </span>
            <span className="text-sm font-medium">
              {SETUP_STEPS[currentStep].title}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4">
            {SETUP_STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              
              return (
                <div 
                  key={step.id} 
                  className={`flex flex-col items-center ${
                    isCompleted ? 'text-primary' : isCurrent ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                    isCompleted ? 'bg-primary text-primary-foreground' : 
                    isCurrent ? 'bg-primary/20 text-primary' : 'bg-muted'
                  }`}>
                    {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                  </div>
                  <span className="text-xs hidden md:block">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Step Content */}
        <Card className="mb-8">
          <CardContent className="p-8">
            {/* Step 1: Enable Widget */}
            {currentStep === 0 && (
              <div data-testid="step-enable">
                <h2 className="text-2xl font-bold mb-2">Enable CI Proof Widget</h2>
                <p className="text-muted-foreground mb-8">
                  Turn on the CI Proof widget to show real-time activity on your product pages.
                </p>
                
                <div className="flex items-center justify-between p-6 bg-muted/50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                      <Eye className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <Label className="text-lg font-semibold">CI Proof Widget</Label>
                      <p className="text-sm text-muted-foreground">
                        Shows "X people viewed, Y purchased" on product pages
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                    data-testid="enable-widget-toggle"
                  />
                </div>
                
                {settings.enabled && (
                  <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-600 text-sm flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Widget enabled. Continue to configure settings.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 2: Choose Scope */}
            {currentStep === 1 && (
              <div data-testid="step-scope">
                <h2 className="text-2xl font-bold mb-2">Choose Scope</h2>
                <p className="text-muted-foreground mb-8">
                  Select which products should display the CI Proof widget.
                </p>
                
                <RadioGroup 
                  value={settings.scope} 
                  onValueChange={(val) => setSettings(prev => ({ ...prev, scope: val }))}
                  className="space-y-4"
                >
                  {/* Free tier option */}
                  <div className={`relative flex items-start gap-4 p-6 rounded-xl border-2 ${
                    settings.scope === 'free' ? 'border-primary bg-primary/5' : 'border-muted'
                  }`}>
                    <RadioGroupItem value="free" id="scope-free" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="scope-free" className="text-lg font-semibold cursor-pointer">
                        Select up to 10 products
                        <Badge variant="outline" className="ml-2">Free</Badge>
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Choose specific products to display the CI Proof widget.
                      </p>
                    </div>
                  </div>
                  
                  {/* Pro tier option */}
                  <div className={`relative flex items-start gap-4 p-6 rounded-xl border-2 ${
                    settings.scope === 'pro' ? 'border-primary bg-primary/5' : 'border-muted'
                  } ${!settings.hasProAddon ? 'opacity-60' : ''}`}>
                    <RadioGroupItem 
                      value="pro" 
                      id="scope-pro" 
                      className="mt-1"
                      disabled={!settings.hasProAddon}
                    />
                    <div className="flex-1">
                      <Label htmlFor="scope-pro" className="text-lg font-semibold cursor-pointer">
                        Full catalog
                        <Badge className="ml-2 bg-primary/20 text-primary">CI Proof Pro</Badge>
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Display CI Proof on all products automatically.
                      </p>
                      
                      {!settings.hasProAddon && (
                        <div className="mt-3 p-3 bg-muted rounded-lg">
                          <p className="text-sm text-muted-foreground mb-2">
                            Upgrade to CI Proof Pro to enable full catalog coverage.
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate('/#pricing')}
                            className="gap-2"
                          >
                            <Sparkles className="w-4 h-4" />
                            View CI Proof Pro
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </RadioGroup>
              </div>
            )}
            
            {/* Step 3: Placement */}
            {currentStep === 2 && (
              <div data-testid="step-placement">
                <h2 className="text-2xl font-bold mb-2">Placement</h2>
                <p className="text-muted-foreground mb-8">
                  Choose where to display the CI Proof widget.
                </p>
                
                <RadioGroup 
                  value={settings.placement} 
                  onValueChange={(val) => setSettings(prev => ({ ...prev, placement: val }))}
                  className="space-y-4"
                >
                  <div className={`flex items-start gap-4 p-6 rounded-xl border-2 ${
                    settings.placement === 'pdp' ? 'border-primary bg-primary/5' : 'border-muted'
                  }`}>
                    <RadioGroupItem value="pdp" id="placement-pdp" className="mt-1" />
                    <div>
                      <Label htmlFor="placement-pdp" className="text-lg font-semibold cursor-pointer">
                        Product Detail Pages (PDP)
                        <Badge variant="outline" className="ml-2">Recommended</Badge>
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Show activity signals on individual product pages.
                      </p>
                    </div>
                  </div>
                  
                  <div className={`flex items-start gap-4 p-6 rounded-xl border-2 ${
                    settings.placement === 'collection' ? 'border-primary bg-primary/5' : 'border-muted'
                  }`}>
                    <RadioGroupItem value="collection" id="placement-collection" className="mt-1" />
                    <div>
                      <Label htmlFor="placement-collection" className="text-lg font-semibold cursor-pointer">
                        Collection Pages
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Show activity signals on collection/category pages.
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            )}
            
            {/* Step 4: Style */}
            {currentStep === 3 && (
              <div data-testid="step-style">
                <h2 className="text-2xl font-bold mb-2">Style</h2>
                <p className="text-muted-foreground mb-8">
                  Customize the appearance of your CI Proof widget.
                </p>
                
                <div className="space-y-8">
                  {/* Theme */}
                  <div>
                    <Label className="text-base font-semibold mb-4 block">Theme</Label>
                    <RadioGroup 
                      value={settings.theme} 
                      onValueChange={(val) => setSettings(prev => ({ ...prev, theme: val }))}
                      className="grid grid-cols-2 gap-4"
                    >
                      <div className={`relative p-6 rounded-xl border-2 text-center ${
                        settings.theme === 'subtle' ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}>
                        <RadioGroupItem value="subtle" id="theme-subtle" className="sr-only" />
                        <Label htmlFor="theme-subtle" className="cursor-pointer block">
                          <div className="w-full h-12 bg-muted/50 rounded-lg mb-3 flex items-center justify-center">
                            <span className="text-xs text-muted-foreground">👁 23 viewing</span>
                          </div>
                          <span className="font-medium">Subtle</span>
                          <p className="text-xs text-muted-foreground mt-1">Blends with your design</p>
                        </Label>
                      </div>
                      
                      <div className={`relative p-6 rounded-xl border-2 text-center ${
                        settings.theme === 'bold' ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}>
                        <RadioGroupItem value="bold" id="theme-bold" className="sr-only" />
                        <Label htmlFor="theme-bold" className="cursor-pointer block">
                          <div className="w-full h-12 bg-primary/20 rounded-lg mb-3 flex items-center justify-center">
                            <span className="text-xs text-primary font-semibold">🔥 23 viewing now</span>
                          </div>
                          <span className="font-medium">Bold</span>
                          <p className="text-xs text-muted-foreground mt-1">Stands out more</p>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  {/* Size */}
                  <div>
                    <Label className="text-base font-semibold mb-4 block">Size</Label>
                    <RadioGroup 
                      value={settings.size} 
                      onValueChange={(val) => setSettings(prev => ({ ...prev, size: val }))}
                      className="grid grid-cols-2 gap-4"
                    >
                      <div className={`relative p-4 rounded-xl border-2 text-center ${
                        settings.size === 'small' ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}>
                        <RadioGroupItem value="small" id="size-small" className="sr-only" />
                        <Label htmlFor="size-small" className="cursor-pointer">
                          <span className="font-medium">Small</span>
                        </Label>
                      </div>
                      
                      <div className={`relative p-4 rounded-xl border-2 text-center ${
                        settings.size === 'medium' ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}>
                        <RadioGroupItem value="medium" id="size-medium" className="sr-only" />
                        <Label htmlFor="size-medium" className="cursor-pointer">
                          <span className="font-medium">Medium</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 5: Verify */}
            {currentStep === 4 && (
              <div data-testid="step-verify">
                <h2 className="text-2xl font-bold mb-2">Verify Installation</h2>
                <p className="text-muted-foreground mb-8">
                  Check that CI Proof is properly installed and receiving events.
                </p>
                
                <div className="space-y-6">
                  {/* Installation snippet */}
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm font-medium mb-2">Installation Instructions</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      If you installed via Shopify, the widget should work automatically. 
                      Otherwise, add this snippet before {"</body>"} on your site:
                    </p>
                    <pre className="text-xs bg-background p-3 rounded border overflow-x-auto">
                      {`<script src="https://cdn.commerceindex.com/proof.js" 
  data-ci-key="YOUR_API_KEY"></script>`}
                    </pre>
                  </div>
                  
                  {/* Verification */}
                  <div className="p-6 bg-muted/50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">Verification Status</p>
                        <p className="text-sm text-muted-foreground">
                          {verificationStatus.checked 
                            ? verificationStatus.isLive 
                              ? `Last event: ${verificationStatus.lastEvent || 'Just now'}`
                              : 'No events detected yet'
                            : 'Click to verify installation'
                          }
                        </p>
                      </div>
                      
                      {verificationStatus.isLive ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Live</span>
                        </div>
                      ) : (
                        <Button 
                          variant="outline" 
                          onClick={checkVerification}
                          data-testid="verify-btn"
                        >
                          Check Status
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Quick test */}
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm font-medium mb-2">Quick Test</p>
                    <p className="text-sm text-muted-foreground mb-3">
                      Open a product page on your store to see the CI Proof widget in action.
                    </p>
                    <Button variant="outline" size="sm" className="gap-2">
                      <ExternalLink className="w-4 h-4" />
                      Open Product Page
                    </Button>
                  </div>
                </div>
                
                {/* Success state */}
                {verificationStatus.isLive && (
                  <div className="mt-6 p-6 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-green-600 mb-1">CI Proof is live ✅</h3>
                    <p className="text-sm text-muted-foreground">
                      Events are being received. Your customers will see real-time activity on your product pages.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={prevStep}
            disabled={currentStep === 0}
            className="gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
            >
              Save & Exit
            </Button>
            
            {currentStep < SETUP_STEPS.length - 1 ? (
              <Button 
                onClick={nextStep}
                disabled={saving || (currentStep === 0 && !settings.enabled)}
                className="gap-2"
                data-testid="next-step-btn"
              >
                {saving ? 'Saving...' : 'Continue'}
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button 
                onClick={completeSetup}
                disabled={saving}
                className="gap-2 bg-green-600 hover:bg-green-700"
                data-testid="complete-setup-btn"
              >
                <CheckCircle className="w-4 h-4" />
                Complete Setup
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
